import React, { useState, useCallback } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  TextField,
  Typography,
  Grid,
  Divider,
  Chip,
} from "@mui/material";
import { Lock, Publish } from "@mui/icons-material";
import { useSearchParams } from "react-router-dom";
import { ThinLayout } from "../Layouts/ThinLayout";
import { BlingCheckmark } from "../Components/BlingCheckmark";
import { DocumentViewer } from "../Components/SubmissionReview/DocumentViewer";
import { QuestionView } from "../Components/Question/View";
import { useQuery, useMutation } from "@apollo/client";
import {
  GET_SUBMISSION_REVIEW_REVIEW_DATA,
  GET_MY_REVIEW_ALLOCATIONS,
  SUBMIT_SUBMISSION_REVIEW,
} from "../Queries";
import { useErrorBoundary } from "../Contexts/ErrorContext";
import { extractErrorMessage } from "../utils/errorUtils";
import { useTranslation } from "react-i18next";

interface RubricAnswer {
  itemId: string;
  value: number | null;
  text: string;
}

export function StudentReviewPage() {
  const [searchParams] = useSearchParams();
  const { setError } = useErrorBoundary();
  const [t] = useTranslation();
  const [answers, setAnswers] = useState<Record<string, RubricAnswer>>({});

  // Decode token from query params
  const key = searchParams.get("key");
  let allocationId = "";
  let reviewToken = "";
  try {
    const decoded = atob(key || "");
    const parts = decoded.split(":");
    allocationId = parts[0];
    reviewToken = parts.slice(1).join(":");
  } catch {
    // Invalid key
  }

  const authHeaders = {
    headers: { Authorization: `JWT ${reviewToken}` },
  };

  const { loading, data, refetch } = useQuery(
    GET_SUBMISSION_REVIEW_REVIEW_DATA,
    {
      variables: { pk: allocationId },
      context: authHeaders,
      skip: !allocationId,
      onCompleted: (result) => {
        // Pre-populate existing answers
        const existing = result?.submissionReviewReviewData?.existingAnswers;
        if (existing) {
          const map: Record<string, RubricAnswer> = {};
          for (const ans of existing) {
            map[ans.itemId] = {
              itemId: ans.itemId,
              value: ans.value,
              text: ans.text,
            };
          }
          setAnswers(map);
        }
      },
      onError: (error) =>
        setError(
          extractErrorMessage(error, String(t("Error loading review"))),
          error,
        ),
    },
  );

  // Fetch all allocations for this activity to enable tab navigation
  const { data: allocationsData, refetch: refetchAllocations } = useQuery(
    GET_MY_REVIEW_ALLOCATIONS,
    {
      variables: { activityId: data?.submissionReviewReviewData?.activityId },
      context: authHeaders,
      skip: !data?.submissionReviewReviewData?.activityId,
    },
  );
  const allAllocations = allocationsData?.myReviewAllocationsForActivity ?? [];

  const [submitReview] = useMutation(SUBMIT_SUBMISSION_REVIEW, {
    context: authHeaders,
    onCompleted: () => {
      refetch();
      // Keep the tab/next-review state in sync so the completion screen
      // reflects this submission immediately
      refetchAllocations();
    },
    onError: (error) =>
      setError(
        extractErrorMessage(error, String(t("Error submitting review"))),
        error,
      ),
  });

  const reviewData = data?.submissionReviewReviewData;

  const updateAnswer = useCallback(
    (itemId: string, field: "value" | "text", val: number | string) => {
      setAnswers((prev) => ({
        ...prev,
        [itemId]: {
          ...prev[itemId],
          itemId,
          value:
            field === "value" ? (val as number) : prev[itemId]?.value ?? null,
          text: field === "text" ? (val as string) : prev[itemId]?.text ?? "",
        },
      }));
    },
    [],
  );

  if (loading) {
    return (
      <ThinLayout title={String(t("Loading..."))} loading={true}>
        <div />
      </ThinLayout>
    );
  }

  if (!reviewData) {
    return (
      <ThinLayout title={String(t("Error"))}>
        <Typography variant="h5">{t("Review not found")}</Typography>
      </ThinLayout>
    );
  }

  // Already submitted — keep the unified flow going: if other reviews for
  // this activity are still pending, guide the student straight to the next
  // one instead of dead-ending on a thank-you screen.
  if (reviewData.submitted) {
    const pendingAllocations = allAllocations.filter(
      (alloc: any) => !alloc.reviewSubmitted,
    );
    const submittedCount = allAllocations.length - pendingAllocations.length;
    const nextPending = pendingAllocations[0];

    return (
      <ThinLayout title="">
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            minHeight: "100vh",
          }}
        >
          <Box sx={{ textAlign: "center", maxWidth: 600 }}>
            <Typography variant="h4" sx={{ mb: 2 }}>
              {nextPending
                ? t("Review submitted!")
                : t("Your review has been submitted. Thank you!")}
            </Typography>
            <BlingCheckmark
              active={false}
              completed={true}
              size="large"
              divProps={{ style: { marginTop: 20 } }}
            />
            {allAllocations.length > 1 && (
              <Typography variant="body1" color="text.secondary" sx={{ mt: 3 }}>
                {submittedCount} {t("of")} {allAllocations.length}{" "}
                {t("reviews completed")}
              </Typography>
            )}
            {nextPending && (
              <Button
                variant="contained"
                component="a"
                href={nextPending.reviewLink}
                sx={{ mt: 3 }}
              >
                {t("Continue to next review")}
              </Button>
            )}
          </Box>
        </Box>
      </ThinLayout>
    );
  }

  const handleSubmit = () => {
    const answerList = Object.values(answers).map((a) => ({
      itemId: a.itemId,
      value: a.value,
      text: a.text,
    }));
    submitReview({
      variables: {
        reviewId: reviewData.id,
        answers: answerList,
      },
    });
  };

  const isLiveReview = reviewData.submissionType === "LIV";

  // Adapt rubric items to the shared survey QuestionView (same Select-based
  // choice inputs and visibility iconography as the response flow). Scale
  // questions without explicit options synthesize 1..scale.
  const toFormItem = (item: any) => ({
    id: item.id,
    category: "",
    itemType: item.itemType,
    questionType: item.questionType,
    label: item.label,
    optional: item.optional,
    options:
      item.options && item.options.length > 0
        ? item.options
        : Array.from({ length: item.scale || 5 }, (_, i) => ({
            id: `${item.id}-${i + 1}`,
            label: String(i + 1),
            value: i + 1,
            order: i + 1,
          })),
    order: item.order,
    recipientType: "",
    subjectType: "",
    scale: item.scale,
    visibility: item.visibility,
  });

  const requiredItems = reviewData.rubricItems.filter(
    (item: any) => !item.optional,
  );
  const allRequiredAnswered = requiredItems.every((item: any) => {
    const answer = answers[item.id];
    if (!answer) return false;
    if (item.itemType === "CH") return answer.value != null;
    if (item.itemType === "TX") return answer.text.trim().length > 0;
    return true;
  });

  return (
    <ThinLayout title={String(t(reviewData.activityName))} fullWidth={true}>
      <Box sx={{ p: 3 }}>
        {/* Review navigation tabs */}
        {allAllocations.length > 1 && (
          <Box sx={{ display: "flex", gap: 1, mb: 2, flexWrap: "wrap" }}>
            {allAllocations.map((alloc: any, idx: number) => (
              <Button
                key={alloc.id}
                variant={alloc.id === allocationId ? "contained" : "outlined"}
                size="small"
                component="a"
                href={alloc.reviewLink}
                sx={{ minWidth: 100 }}
              >
                {alloc.subjectLabel ?? `${t("Review")} ${idx + 1}`}
                {alloc.reviewSubmitted && " ✓"}
              </Button>
            ))}
          </Box>
        )}

        {/* Live presentations have no document to preview — give the whole
            width to the review form and keep the presenter front and center */}
        {isLiveReview && (
          <Box sx={{ textAlign: "center", mb: 3 }}>
            <Typography variant="h4">
              {reviewData.presentationSubject}
            </Typography>
            <Typography color="text.secondary">
              {t(
                "You are reviewing this live presentation. Rate it using the rubric.",
              )}
            </Typography>
          </Box>
        )}

        <Grid container spacing={3}>
          {/* Left: Submission being reviewed (not shown for live
              presentations) */}
          {!isLiveReview && (
            <Grid item xs={12} md={6}>
              <Card sx={{ height: "100%" }}>
                <CardHeader title={String(t("Submission Under Review"))} />
                <CardContent>
                  {reviewData.submissionFileUrl && (
                    <Box sx={{ mb: 2 }}>
                      <DocumentViewer fileUrl={reviewData.submissionFileUrl} />
                    </Box>
                  )}
                  {reviewData.submissionText && (
                    <Box>
                      <Typography
                        variant="subtitle2"
                        color="text.secondary"
                        sx={{ mb: 1 }}
                      >
                        {t("Submission Text")}
                      </Typography>
                      <Typography
                        variant="body1"
                        sx={{ whiteSpace: "pre-wrap" }}
                      >
                        {reviewData.submissionText}
                      </Typography>
                    </Box>
                  )}
                  {!reviewData.submissionFileUrl &&
                    !reviewData.submissionText && (
                      <Typography color="text.secondary">
                        {t("No submission content available.")}
                      </Typography>
                    )}
                </CardContent>
              </Card>
            </Grid>
          )}

          {/* Rubric form */}
          <Grid item xs={12} md={isLiveReview ? 12 : 6}>
            <Card>
              <CardHeader
                title={String(t("Your Review"))}
                action={
                  <Chip
                    icon={<Lock fontSize="small" />}
                    label={String(
                      t(
                        {
                          DB: "Double-blind",
                          SB: "Single-blind",
                          ID: "Identified",
                        }[reviewData.anonymityMode as string] ?? "Double-blind",
                      ),
                    )}
                    size="small"
                    variant="outlined"
                  />
                }
              />
              <CardContent>
                {/* Review progress */}
                {reviewData.totalReviews > 1 && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ mb: 2, display: "block", textAlign: "center" }}
                  >
                    {t("Review")} {reviewData.reviewNumber} {t("of")}{" "}
                    {reviewData.totalReviews}
                  </Typography>
                )}

                {/* Due date warning */}
                {reviewData.reviewDueDate && (
                  <Typography
                    variant="body2"
                    color={
                      new Date() > new Date(reviewData.reviewDueDate)
                        ? "error"
                        : "text.secondary"
                    }
                    sx={{ mb: 1 }}
                  >
                    {t("Due")}:{" "}
                    {new Date(reviewData.reviewDueDate).toLocaleDateString()}{" "}
                    {t("at")}{" "}
                    {new Date(reviewData.reviewDueDate).toLocaleTimeString()}
                    {new Date() > new Date(reviewData.reviewDueDate) &&
                      ` (${String(t("OVERDUE"))})`}
                  </Typography>
                )}
                {reviewData.reviewDueDate &&
                  new Date() > new Date(reviewData.reviewDueDate) && (
                    <Alert severity="warning" sx={{ mb: 2 }}>
                      {t("This review is past due, but you can still submit.")}
                    </Alert>
                  )}

                {reviewData.activityDescription && (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 3 }}
                  >
                    {reviewData.activityDescription}
                  </Typography>
                )}

                {reviewData.rubricItems.map((item: any, index: number) => (
                  <Box key={item.id} sx={{ mb: 3 }}>
                    {index > 0 && <Divider sx={{ mb: 2 }} />}

                    {item.itemType === "IN" && (
                      <QuestionView
                        formItem={toFormItem(item) as any}
                        hideRecipientIndicators
                      />
                    )}

                    {item.itemType === "CH" && (
                      <QuestionView
                        formItem={toFormItem(item) as any}
                        value={answers[item.id]?.value ?? undefined}
                        onValueChange={(v) =>
                          updateAnswer(item.id, "value", Number(v))
                        }
                        hideRecipientIndicators
                      />
                    )}

                    {item.itemType === "TX" && (
                      <Box>
                        <Typography
                          variant="body2"
                          sx={{ mb: 1 }}
                          id={`rubric-${item.id}-label`}
                        >
                          {item.label}
                          {!item.optional && " *"}
                        </Typography>
                        <TextField
                          id={`rubric-${item.id}-input`}
                          multiline
                          rows={3}
                          fullWidth
                          placeholder={String(t("Enter your response..."))}
                          value={answers[item.id]?.text ?? ""}
                          onChange={(e) =>
                            updateAnswer(item.id, "text", e.target.value)
                          }
                          inputProps={{
                            "aria-labelledby": `rubric-${item.id}-label`,
                            "aria-required": !item.optional,
                          }}
                        />
                        <Typography
                          variant="caption"
                          color={
                            (answers[item.id]?.text ?? "")
                              .split(/\s+/)
                              .filter(Boolean).length < 10
                              ? "warning.main"
                              : "text.secondary"
                          }
                          sx={{ mt: 0.5 }}
                        >
                          {
                            (answers[item.id]?.text ?? "")
                              .split(/\s+/)
                              .filter(Boolean).length
                          }{" "}
                          {t("words")}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                ))}

                <Box sx={{ mt: 3 }}>
                  <Button
                    variant="contained"
                    onClick={handleSubmit}
                    disabled={!allRequiredAnswered}
                    startIcon={<Publish />}
                  >
                    {t("Submit Review")}
                  </Button>
                  {!allRequiredAnswered && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ ml: 2 }}
                    >
                      {t("Please complete all required questions.")}
                    </Typography>
                  )}
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>
    </ThinLayout>
  );
}
